/**
 * ScriptToolbar
 *
 * Toolbar for the Scripts panel with "Open", "Mount" buttons and search input.
 */

import { useCallback, useState } from 'react';
import { FolderPlus, FolderOpen, Search, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from '../../../components/ui/toaster';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { isQueryTab } from '../../workspace/core/types/workspaceTypes';
import { useScriptBrowserStore } from '../store/scriptBrowserStore';
import { openSqlFile } from '../../workspace/query-editor/services/queryFilesApi';
import { useContextSelectionStore } from '../../toolbar-context';
import { useDatabaseUIStore } from '../../database-objects/store/uiStore';
import {
  getFileNameFromPath,
  hashText,
} from '../../workspace/query-editor/utils/queryFileUtils';
import { scanMountedFolder } from '../services/mountedFolderScanService';
import { selectScriptBrowserDirectory } from '../services/scriptBrowserIpcApi';
import { useRestrictedFeatureDialog } from '../../browser-mode/hooks/useRestrictedFeatureDialog';
import { RestrictedFeatureCTADialog } from '../../browser-mode/components/RestrictedFeatureCTADialog';
import { readBlueprintMetadata } from '../../database-blueprint/services/databaseBlueprintApi';
import { openDatabaseBlueprintTab } from '../../database-blueprint/services/openDatabaseBlueprintTab';
import { MountAsBlueprintDialog } from './MountAsBlueprintDialog';
import type { BlueprintMetadata } from '../../database-blueprint/types/databaseBlueprintTypes';
import { isBetaFeatureEnabled } from '@/core/beta';

/** Normalize file path for comparison. */
const normalizePath = (p: string): string => p.replace(/\\/g, '/').trim();

type ScriptToolbarProps = {
  searchQuery: string;
  onSearchChange: (query: string) => void;
};

export function ScriptToolbar({
  searchQuery,
  onSearchChange,
}: ScriptToolbarProps) {
  const addTab = useWorkspaceStore((s) => s.addTab);
  const setActiveTab = useWorkspaceStore((s) => s.setActiveTab);
  const addMountedFolder = useScriptBrowserStore((s) => s.addMountedFolder);
  const markFolderAsBlueprint = useScriptBrowserStore((s) => s.markFolderAsBlueprint);
  const requestContext = useContextSelectionStore((s) => s.requestContext);
  const { checkAndBlock, isOpen, feature, closeDialog } = useRestrictedFeatureDialog();

  const [blueprintPrompt, setBlueprintPrompt] = useState<{
    folderPath: string;
    metadata: BlueprintMetadata;
  } | null>(null);

  const handleOpenFile = useCallback(async () => {
    try {
      const result = await openSqlFile({});
      if (!result.success) {
        if (!result.cancelled) {
          toast.error({ title: 'Failed to open file', description: result.error });
        }
        return;
      }

      const filePath = result.filePath ?? '';
      const normalized = normalizePath(filePath);

      // Dedup: activate existing tab if already open. Read tabs via getState()
      // instead of subscribing — this callback only needs the value at call time,
      // and subscribing would re-render the toolbar on every keystroke.
      const existing = useWorkspaceStore.getState().tabs.find(
        (t) => isQueryTab(t) && normalizePath(t.content.filePath ?? '') === normalized
      );
      if (existing) {
        setActiveTab(existing.id);
        return;
      }

      const query = result.query ?? '';

      const context = await requestContext(
        { requireConnection: true, requireDatabase: false },
        'Open Script'
      );

      const { selectedDatabase, selectedDatabaseConnectionId } =
        useDatabaseUIStore.getState();

      const connectionInfo =
        context
          ? {
              connectionId: context.connectionId,
              connectionName: context.connectionName,
              serverHost: context.serverHost,
              database:
                selectedDatabaseConnectionId === context.connectionId
                  ? selectedDatabase
                  : undefined,
            }
          : undefined;

      addTab({
        type: 'query',
        title: getFileNameFromPath(filePath),
        content: { query, filePath, fileLastSavedHash: hashText(query) },
        ...(connectionInfo ? { connectionInfo } : {}),
      });

      useScriptBrowserStore.getState().recordScriptOpened(filePath, query.slice(0, 100));
    } catch (err) {
      toast.error({
        title: 'Failed to open file',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, [addTab, setActiveTab, requestContext]);

  const mountAsPlainFolder = useCallback((directoryPath: string) => {
    addMountedFolder(directoryPath);
    const store = useScriptBrowserStore.getState();
    const folders = store.activeWorkspaceId
      ? store.workspaces.find((w) => w.id === store.activeWorkspaceId)?.mountedFolders ?? []
      : store.defaultWorkspaceState.mountedFolders;
    const added = folders[folders.length - 1];
    if (added) scanMountedFolder(added);
  }, [addMountedFolder]);

  const handleMount = useCallback(async () => {
    if (checkAndBlock('mountFolder')) return;
    try {
      const result = await selectScriptBrowserDirectory();
      if (result.error) {
        toast.error({ title: 'Failed to open folder picker', description: result.error });
        return;
      }
      if (result.cancelled || !result.directoryPath) return;

      // Blueprint detection: if the folder holds a valid jamsql-blueprint.json,
      // prompt before treating it as a plain script folder. Skipped when the
      // Database Blueprint beta feature is off — offering to mount as a
      // blueprint is a new entry point into a feature the user turned off, so
      // the folder mounts as a plain script folder instead. Folders already
      // mounted as blueprints keep working, like an already-open tab does.
      if (isBetaFeatureEnabled('database-blueprint')) {
        const meta = await readBlueprintMetadata({ folderPath: result.directoryPath });
        if (meta.success) {
          setBlueprintPrompt({ folderPath: result.directoryPath, metadata: meta.metadata });
          return;
        }
      }
      mountAsPlainFolder(result.directoryPath);
    } catch (err) {
      toast.error({
        title: 'Failed to mount folder',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, [checkAndBlock, mountAsPlainFolder]);

  const handleMountAsBlueprint = useCallback(() => {
    if (!blueprintPrompt) return;
    const { folderPath, metadata } = blueprintPrompt;
    const label =
      metadata.linkedDbs[0]?.label ??
      folderPath.split('/').filter(Boolean).pop() ??
      'Blueprint';
    const folderId = markFolderAsBlueprint(folderPath, label, {
      version: 1,
      linkedDbs: metadata.linkedDbs.map((l) => ({
        label: l.label,
        connectionId: l.connectionId,
        connectionDisplayName: l.connectionDisplayName,
        database: l.database,
        engine: l.engine,
        lastSyncAt: l.lastSyncAt,
      })),
      excludedObjects: metadata.excludedObjects ?? [],
      ...(metadata.activeLinkLabel ? { activeLinkLabel: metadata.activeLinkLabel } : {}),
    });
    window.electronAPI?.analytics
      ?.track('feature:database_blueprint_mount_detected', { accepted: true })
      .catch(() => {});
    openDatabaseBlueprintTab(folderId, label);
    setBlueprintPrompt(null);
  }, [blueprintPrompt, markFolderAsBlueprint]);

  const handleMountAsPlainFromPrompt = useCallback(() => {
    if (!blueprintPrompt) return;
    mountAsPlainFolder(blueprintPrompt.folderPath);
    window.electronAPI?.analytics
      ?.track('feature:database_blueprint_mount_detected', { accepted: false })
      .catch(() => {});
    setBlueprintPrompt(null);
  }, [blueprintPrompt, mountAsPlainFolder]);

  return (
    <>
      <div className="flex items-center gap-1 px-2 py-1.5 select-none">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          onClick={handleOpenFile}
          title="Open SQL file"
          data-testid="script-toolbar-open"
        >
          <FolderOpen className="h-3.5 w-3.5" />
          Open
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={handleMount}
          title="Mount folder"
          data-testid="script-toolbar-mount"
        >
          <FolderPlus className="h-3.5 w-3.5" />
        </Button>
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            placeholder="Filter scripts..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-7 pl-7 pr-7 text-xs"
            data-testid="script-toolbar-search"
          />
          {searchQuery && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-sm hover:bg-accent cursor-pointer"
              onClick={() => onSearchChange('')}
              title="Clear search"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>
      <RestrictedFeatureCTADialog
        open={isOpen}
        feature={feature || 'mountFolder'}
        onClose={closeDialog}
      />
      <MountAsBlueprintDialog
        open={!!blueprintPrompt}
        onOpenChange={(o) => { if (!o) setBlueprintPrompt(null); }}
        onMountAsBlueprint={handleMountAsBlueprint}
        onMountAsPlainFolder={handleMountAsPlainFromPrompt}
      />
    </>
  );
}
